import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, CreditCard, Wallet, Loader2, CheckCircle, AlertTriangle, Plus, ArrowRight, Info, ExternalLink } from 'lucide-react';
import { paymentsApi } from '../lib/api';
import { toast } from 'sonner';
import StripeCheckoutModal from './StripeCheckoutModal';

const MIN_CARD_CHARGE = 1.00;
const PENDING_PURCHASE_KEY = 'reframes_pending_purchase';

// Save purchase state for cross-page navigation (add card → return)
export const savePendingPurchase = (data) => {
  sessionStorage.setItem(PENDING_PURCHASE_KEY, JSON.stringify({
    ...data,
    returnTo: window.location.pathname + window.location.search,
    timestamp: Date.now(),
  }));
};

export const getPendingPurchase = () => {
  try {
    const raw = sessionStorage.getItem(PENDING_PURCHASE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    // Expire after 30 min
    if (Date.now() - data.timestamp > 30 * 60 * 1000) {
      sessionStorage.removeItem(PENDING_PURCHASE_KEY);
      return null;
    }
    return data;
  } catch { return null; }
};

export const clearPendingPurchase = () => {
  sessionStorage.removeItem(PENDING_PURCHASE_KEY);
};

const PaymentMethodModal = ({
  isOpen,
  onClose,
  paymentType,
  amount,
  creatorId,
  filmId,
  productId,
  creatorName,
  onSuccess,
  metadata = {},
  originUrl,
  shopCheckoutFn,
}) => {
  const [card, setCard] = useState(null);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [showTopUp, setShowTopUp] = useState(false);
  const [topUpOpen, setTopUpOpen] = useState(false);

  const isDonationUnder5 = paymentType === 'donation' && amount < 5;
  const isPPVUnder2 = paymentType === 'premium_content' && amount < 2;
  const isSmallPaymentBalanceOnly = isDonationUnder5 || isPPVUnder2;
  const isSubscription = paymentType === 'subscription';
  const isMerch = paymentType === 'merchandise';
  const canUseBalance = !isMerch && balance >= amount;
  const hasPartialBalance = !isMerch && balance > 0 && balance < amount;
  const canSplit = hasPartialBalance && card && !isSubscription && !isSmallPaymentBalanceOnly && !isMerch;

  // Split calculations
  const balancePortion = canSplit ? Math.min(balance, amount) : 0;
  const cardRaw = canSplit ? Math.round((amount - balancePortion) * 100) / 100 : 0;
  const cardCharge = canSplit ? Math.max(cardRaw, MIN_CARD_CHARGE) : 0;
  const creditBack = canSplit && cardCharge > cardRaw ? Math.round((cardCharge - cardRaw) * 100) / 100 : 0;

  const fetchInfo = useCallback(async () => {
    setLoading(true);
    try {
      const data = await paymentsApi.getPaymentInfo();
      setCard(data.card);
      setBalance(data.balance || 0);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setResult(null);
      setSelectedMethod(null);
      setShowTopUp(false);
      setTopUpOpen(false);
      return;
    }
    fetchInfo();
  }, [isOpen, fetchInfo]);

  // Auto-select best method after data loads
  useEffect(() => {
    if (loading || !isOpen) return;
    if (isMerch) {
      // Merch: card only (Direct Charge on creator)
      setSelectedMethod(card ? 'card' : null);
    } else if (isSmallPaymentBalanceOnly) {
      setSelectedMethod(canUseBalance ? 'balance' : null);
    } else if (canUseBalance) {
      setSelectedMethod('balance');
    } else if (canSplit) {
      setSelectedMethod('split');
    } else if (card) {
      setSelectedMethod('card');
    }
  }, [loading, isOpen, isMerch, isSmallPaymentBalanceOnly, canUseBalance, canSplit, card]);

  if (!isOpen) return null;
  const origin = originUrl || window.location.origin;
  const portalRoot = document.body;

  const handleConfirm = async () => {
    if (!selectedMethod) { toast.error('Select a payment method'); return; }
    setProcessing(true);
    try {
      let res;
      if (shopCheckoutFn) {
        res = await shopCheckoutFn(selectedMethod);
      } else if (isSubscription) {
        // Subscriptions use dedicated endpoint
        res = await paymentsApi.subscribe('premium', origin, selectedMethod);
      } else {
        res = await paymentsApi.processPayment({
          payment_type: paymentType,
          payment_method: selectedMethod,
          amount: paymentType === 'donation' ? amount : undefined,
          creator_id: creatorId || '',
          film_id: filmId || '',
          product_id: productId || '',
          origin_url: origin,
          metadata,
        });
      }
      if (res.status === 'completed' || res.status === 'active') {
        setResult(res);
        if (onSuccess) onSuccess(res);
      } else if (res.checkout_url || res.status === 'checkout_required') {
        window.location.href = res.checkout_url;
      }
    } catch (error) {
      const errorData = error.data;
      if (errorData?.error === 'insufficient_balance') {
        setBalance(errorData.balance || 0);
        setShowTopUp(true);
      } else {
        toast.error(error.message || 'Payment failed');
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleTopUpComplete = async () => {
    setTopUpOpen(false);
    setShowTopUp(false);
    await fetchInfo();
  };

  const handleAddCardRedirect = () => {
    savePendingPurchase({ paymentType, amount, creatorId, filmId, productId, creatorName, metadata });
    window.location.href = `${window.location.origin}/profile?action=add_card`;
  };

  const handleTopUpClick = () => {
    if (card) {
      setTopUpOpen(true);
    } else {
      savePendingPurchase({ paymentType, amount, creatorId, filmId, productId, creatorName, metadata });
      window.location.href = `${window.location.origin}/profile?action=add_card&then=topup`;
    }
  };

  const getTitle = () => {
    switch (paymentType) {
      case 'donation': return 'Send Donation';
      case 'subscription': return 'Subscribe to Premium';
      case 'premium_content': return 'Unlock Content';
      case 'merchandise': return 'Complete Purchase';
      default: return 'Payment';
    }
  };

  const getConfirmLabel = () => {
    if (selectedMethod === 'split') return `Pay ${amount.toFixed(2)} USD (Split)`;
    if (selectedMethod === 'balance') return `Pay ${amount.toFixed(2)} USD from Balance`;
    if (selectedMethod === 'card') return `Pay ${amount.toFixed(2)} USD with Card`;
    return 'Confirm Payment';
  };

  // --- SUCCESS CONFIRMATION ---
  if (result) {
    const isSplit = result.payment_method === 'split';
    return createPortal(
      <div className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="payment-confirmation-modal">
        <div className="bg-[#0F0B1E] border border-[#8B5CF6]/20 rounded-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
          <div className="p-8 text-center">
            <CheckCircle className="w-14 h-14 text-[#00FF9D] mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }} data-testid="confirmation-title">
              Payment Successful!
            </h2>
            <div className="space-y-2 mb-6">
              <p className="text-2xl font-bold text-white" data-testid="confirmation-amount">
                {result.amount?.toFixed(2)} <span className="text-base text-gray-400 font-normal">USD</span>
              </p>
              {isSplit ? (
                <div className="text-sm text-gray-400 space-y-1" data-testid="confirmation-method">
                  <p>Balance used: <span className="text-white">{result.balance_used?.toFixed(2)} USD</span></p>
                  <p>Card charged: <span className="text-white">{result.card_charged?.toFixed(2)} USD</span></p>
                  {result.credit_back > 0 && (
                    <p className="text-[#6EE7A8] text-xs">+{result.credit_back.toFixed(2)} USD credited back to balance</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-400" data-testid="confirmation-method">
                  Paid via {result.payment_method === 'balance' ? 'Balance' : result.payment_method === 'card' ? 'Saved Card' : result.payment_method}
                </p>
              )}
              {result.new_balance !== undefined && (
                <p className="text-xs text-[#6EE7A8]">Remaining balance: {result.new_balance.toFixed(2)} USD</p>
              )}
            </div>
            <button onClick={onClose}
              className="w-full py-3 bg-[#8B5CF6] hover:bg-[#7C3AED] rounded-xl text-white font-semibold transition-colors"
              data-testid="confirmation-done-btn">Done</button>
          </div>
        </div>
      </div>,
      portalRoot
    );
  }

  // --- TOP UP SUB-FLOW ---
  if (showTopUp) {
    return createPortal(
      <>
        <div className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4" data-testid="insufficient-balance-modal">
          <div className="bg-[#0F0B1E] border border-yellow-500/30 rounded-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-6 text-center">
              <AlertTriangle className="w-12 h-12 text-yellow-400 mx-auto mb-3" />
              <h2 className="text-lg font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Insufficient Balance
              </h2>
              <p className="text-sm text-gray-400 mb-2">
                You need <span className="text-white font-semibold">{amount.toFixed(2)} USD</span> but only have{' '}
                <span className="text-yellow-400 font-semibold">{balance.toFixed(2)} USD</span>
              </p>
              <p className="text-xs text-gray-500 mb-5">Add funds to your balance to complete this payment.</p>
              <button onClick={handleTopUpClick}
                className="w-full py-3 bg-[#6EE7A8]/15 hover:bg-[#6EE7A8]/25 text-[#6EE7A8] border border-[#6EE7A8]/30 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors mb-3"
                data-testid="topup-balance-btn">
                <Plus className="w-4 h-4" /> Add Balance
              </button>
              {!card && (
                <p className="text-xs text-gray-500 mb-3">You'll need to add a card first to top up.</p>
              )}
              <button onClick={() => setShowTopUp(false)}
                className="w-full py-2.5 text-gray-400 hover:text-white text-sm transition-colors"
                data-testid="topup-cancel-btn">Cancel</button>
            </div>
          </div>
        </div>
        <StripeCheckoutModal
          isOpen={topUpOpen}
          onClose={() => setTopUpOpen(false)}
          paymentType="add_balance"
          onSuccess={handleTopUpComplete}
        />
      </>,
      portalRoot
    );
  }

  // --- MAIN SELECTION UI ---
  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="payment-method-modal">
      <div className="bg-[#0F0B1E] border border-[#8B5CF6]/20 rounded-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-5 border-b border-white/5 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }} data-testid="payment-modal-title">{getTitle()}</h2>
            <p className="text-xs text-gray-500 mt-0.5">Choose payment method</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors" data-testid="payment-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-[#8B5CF6] animate-spin" />
            </div>
          ) : (
            <>
              {/* Amount */}
              <div className="text-center mb-5">
                <p className="text-3xl font-bold text-white" data-testid="payment-amount">
                  {amount?.toFixed(2)} <span className="text-base text-gray-400 font-normal">USD</span>
                </p>
                {creatorName && <p className="text-xs text-gray-500 mt-1">to {creatorName}</p>}
              </div>

              {isSmallPaymentBalanceOnly && (
                <div className="mb-4 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                  <p className="text-xs text-yellow-400 text-center">
                    {isDonationUnder5 ? 'Donations under $5' : 'Purchases under $2'} are processed from your balance
                  </p>
                </div>
              )}

              {isMerch && (
                <div className="mb-4 p-3 rounded-lg bg-[#8B5CF6]/10 border border-[#8B5CF6]/20">
                  <p className="text-xs text-[#8B5CF6] text-center">
                    Merchandise purchases are processed via card payment directly to the creator
                  </p>
                </div>
              )}

              <div className="space-y-2 mb-5">
                {/* OPTION: Full Balance */}
                {canUseBalance && (
                  <MethodOption
                    selected={selectedMethod === 'balance'}
                    onClick={() => setSelectedMethod('balance')}
                    icon={<Wallet className={`w-5 h-5 ${selectedMethod === 'balance' ? 'text-[#6EE7A8]' : 'text-gray-400'}`} />}
                    iconBg={selectedMethod === 'balance' ? 'bg-[#6EE7A8]/20' : 'bg-white/5'}
                    borderColor={selectedMethod === 'balance' ? 'border-[#6EE7A8]' : 'border-white/10'}
                    accentColor="#6EE7A8"
                    title="Pay from Balance"
                    subtitle={`${balance.toFixed(2)} USD available`}
                    testId="select-balance-method"
                  />
                )}

                {/* OPTION: Full Card (always show for non-restricted payments if card exists) */}
                {!isSmallPaymentBalanceOnly && card && (
                  <MethodOption
                    selected={selectedMethod === 'card'}
                    onClick={() => setSelectedMethod('card')}
                    icon={<CreditCard className={`w-5 h-5 ${selectedMethod === 'card' ? 'text-[#8B5CF6]' : 'text-gray-400'}`} />}
                    iconBg={selectedMethod === 'card' ? 'bg-[#8B5CF6]/20' : 'bg-white/5'}
                    borderColor={selectedMethod === 'card' ? 'border-[#8B5CF6]' : 'border-white/10'}
                    accentColor="#8B5CF6"
                    title={`${card.brand} **** ${card.last4}`}
                    subtitle={`Expires ${card.exp_month}/${card.exp_year}`}
                    testId="select-card-method"
                  />
                )}

                {/* OPTION: Split Balance + Card */}
                {canSplit && (
                  <button
                    onClick={() => setSelectedMethod('split')}
                    className={`w-full p-4 rounded-xl border text-left transition-all ${
                      selectedMethod === 'split'
                        ? 'border-[#F59E0B] bg-[#F59E0B]/10'
                        : 'border-white/10 bg-white/[0.02] hover:border-white/20'
                    }`}
                    data-testid="select-split-method">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        selectedMethod === 'split' ? 'bg-[#F59E0B]/20' : 'bg-white/5'
                      }`}>
                        <div className="relative">
                          <Wallet className={`w-4 h-4 ${selectedMethod === 'split' ? 'text-[#F59E0B]' : 'text-gray-400'}`} />
                          <CreditCard className={`w-3 h-3 absolute -bottom-1 -right-1 ${selectedMethod === 'split' ? 'text-[#F59E0B]' : 'text-gray-500'}`} />
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">Balance + Card</p>
                        <p className="text-xs text-gray-500">Use balance and pay remainder with card</p>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedMethod === 'split' ? 'border-[#F59E0B]' : 'border-white/20'
                      }`}>
                        {selectedMethod === 'split' && <div className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]" />}
                      </div>
                    </div>
                    {/* Split breakdown */}
                    <div className="ml-[52px] text-xs space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-400">From balance:</span>
                        <span className="text-[#6EE7A8] font-medium">-{balancePortion.toFixed(2)} USD</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Card charge:</span>
                        <span className="text-[#8B5CF6] font-medium">{cardCharge.toFixed(2)} USD</span>
                      </div>
                      {creditBack > 0 && (
                        <div className="flex justify-between items-start">
                          <span className="text-gray-500 flex items-center gap-1">
                            <Info className="w-3 h-3" />Min. card charge $1.00:
                          </span>
                          <span className="text-[#6EE7A8] font-medium">+{creditBack.toFixed(2)} USD back</span>
                        </div>
                      )}
                    </div>
                  </button>
                )}

                {/* Insufficient balance for balance-only payments */}
                {isSmallPaymentBalanceOnly && !canUseBalance && (
                  <div className="p-4 rounded-xl border border-yellow-500/20 bg-yellow-500/5">
                    <div className="flex items-center gap-3 mb-3">
                      <Wallet className="w-5 h-5 text-yellow-400" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-yellow-400">Insufficient Balance</p>
                        <p className="text-xs text-gray-500">{balance.toFixed(2)} / {amount.toFixed(2)} USD needed</p>
                      </div>
                    </div>
                    <button onClick={handleTopUpClick}
                      className="w-full py-2.5 bg-[#6EE7A8]/15 hover:bg-[#6EE7A8]/25 text-[#6EE7A8] border border-[#6EE7A8]/30 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
                      data-testid="inline-topup-btn">
                      <Plus className="w-3.5 h-3.5" /> Add Balance
                    </button>
                  </div>
                )}

                {/* Subscription: insufficient balance, no split allowed */}
                {isSubscription && hasPartialBalance && !canUseBalance && (
                  <div className="p-3 rounded-lg bg-yellow-500/5 border border-yellow-500/15">
                    <p className="text-xs text-yellow-400 text-center flex items-center justify-center gap-1">
                      <Info className="w-3 h-3" /> Subscriptions require full payment — split not available
                    </p>
                  </div>
                )}

                {/* No card, no balance */}
                {!isSmallPaymentBalanceOnly && !card && !canUseBalance && (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-400 mb-3">No payment method available</p>
                    <div className="flex gap-2">
                      <button onClick={handleAddCardRedirect}
                        className="flex-1 py-2.5 bg-[#8B5CF6]/15 hover:bg-[#8B5CF6]/25 text-[#8B5CF6] border border-[#8B5CF6]/30 rounded-lg text-sm font-semibold flex items-center justify-center gap-1 transition-colors"
                        data-testid="go-add-card-btn">
                        <CreditCard className="w-3.5 h-3.5" /> Add Card
                      </button>
                      <button onClick={handleTopUpClick}
                        className="flex-1 py-2.5 bg-[#6EE7A8]/15 hover:bg-[#6EE7A8]/25 text-[#6EE7A8] border border-[#6EE7A8]/30 rounded-lg text-sm font-semibold flex items-center justify-center gap-1 transition-colors"
                        data-testid="go-topup-btn">
                        <Wallet className="w-3.5 h-3.5" /> Add Balance
                      </button>
                    </div>
                  </div>
                )}

                {/* Has partial balance but no card → prompt to add card */}
                {hasPartialBalance && !card && !isDonationUnder5 && !isSubscription && (
                  <div className="p-4 rounded-xl border border-[#8B5CF6]/20 bg-[#8B5CF6]/5">
                    <p className="text-sm text-gray-300 mb-2">You have <span className="text-[#6EE7A8] font-semibold">{balance.toFixed(2)} USD</span> in balance.</p>
                    <p className="text-xs text-gray-500 mb-3">Add a card to use balance + card split payment.</p>
                    <button onClick={handleAddCardRedirect}
                      className="w-full py-2.5 bg-[#8B5CF6]/15 hover:bg-[#8B5CF6]/25 text-[#8B5CF6] border border-[#8B5CF6]/30 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
                      data-testid="add-card-for-split-btn">
                      <CreditCard className="w-3.5 h-3.5" /> Add Card <ExternalLink className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {!loading && selectedMethod && (
          <div className="p-5 border-t border-white/5">
            <button onClick={handleConfirm} disabled={processing}
              className="w-full py-3 rounded-xl bg-[#8B5CF6] hover:bg-[#7C3AED] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold flex items-center justify-center gap-2 transition-colors"
              data-testid="confirm-payment-btn">
              {processing ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
              ) : (
                <>{getConfirmLabel()} <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
            <p className="text-[10px] text-gray-600 text-center mt-2">
              {selectedMethod === 'balance' ? 'Amount will be deducted from your balance'
                : selectedMethod === 'split' ? `${balancePortion.toFixed(2)} USD from balance + ${cardCharge.toFixed(2)} USD from card`
                : 'Payment processed securely via Stripe'}
            </p>
          </div>
        )}
      </div>

      {/* Inline top-up modal */}
      <StripeCheckoutModal
        isOpen={topUpOpen}
        onClose={() => setTopUpOpen(false)}
        paymentType="add_balance"
        onSuccess={handleTopUpComplete}
      />
    </div>,
    portalRoot
  );
};

// Reusable radio option component
const MethodOption = ({ selected, onClick, icon, iconBg, borderColor, accentColor, title, subtitle, testId }) => (
  <button
    onClick={onClick}
    className={`w-full p-4 rounded-xl border text-left transition-all flex items-center gap-3 ${
      selected ? `${borderColor} bg-[${accentColor}]/10` : 'border-white/10 bg-white/[0.02] hover:border-white/20'
    }`}
    data-testid={testId}>
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconBg}`}>{icon}</div>
    <div className="flex-1">
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs text-gray-500">{subtitle}</p>
    </div>
    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selected ? borderColor : 'border-white/20'}`}>
      {selected && <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: accentColor }} />}
    </div>
  </button>
);

export default PaymentMethodModal;
